import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthField, AuthShell, ErrorNote, SubmitButton } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return setInvite(false);
    api.get(`/auth/invite/${encodeURIComponent(token)}`).then((r) => setInvite(r.data)).catch(() => setInvite(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      await acceptInvite(token, form.password);
      navigate("/players");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (invite === null) return <AuthShell eyebrow="Invitation" title="Checking your invitation" />;
  if (invite === false) {
    return (
      <AuthShell eyebrow="Invitation" title="Link not valid">
        <div data-testid="invite-invalid" className="space-y-6 text-sm text-slate-400">
          <p>This invitation link is invalid, has already been used or has expired.</p>
          <div className="flex gap-6">
            <Link to="/request-access" data-testid="invite-request-new-link" className="font-semibold text-white underline-offset-4 hover:underline">Request a new invitation</Link>
            <Link to="/login" data-testid="invite-login-link" className="font-semibold text-white underline-offset-4 hover:underline">Log in</Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="You're invited" title="Create your account">
      <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
        <p data-testid="invite-welcome" className="text-sm text-slate-400">Welcome, <span className="font-semibold text-white">{invite.name}</span>. Your account will use <span className="font-semibold text-white">{invite.email}</span>.</p>
        <AuthField label="Password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          required minLength={6} autoComplete="new-password" data-testid="invite-password-input" />
        <AuthField label="Confirm password" type="password" value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
          required autoComplete="new-password" data-testid="invite-password-confirm-input" />
        <ErrorNote text={error} />
        <SubmitButton busy={busy} testId="accept-invite-submit-button">Create account</SubmitButton>
      </form>
    </AuthShell>
  );
}
